import { useCallback, useMemo, useRef, useState, type SetStateAction } from "react";
import {
  blankPlanDraft,
  newProgramToProgram,
  planToDraft,
  programToDraft,
  type Draft,
  type NewProgram,
  type Plan,
  type PlanSeed,
  type Program,
  type UnitsPreference,
  DEFAULT_UNITS_PREFERENCE,
  uuidv4,
} from "@heraion/shared";

import { useLocale } from "../primitives/useLocale";
import { useHistoryState } from "../primitives/useHistoryState";
import type { PersistedSnapshot } from "./types";
import type { EditorDraftBlob } from "./editorDraftStore";

// The editor's draft STATE, split from useProgramEditor (#336), which keeps
// the save path. Three things live here:
//  - the seed: from the server's program/plan, a creation seed, or a draft
//    resumed from the device (in the unit it was written in);
//  - the history: one useHistoryState entry holds draft AND scheduledFor,
//    above every view, so a day, week or tab switch never loses undo;
//  - the identity on the server: the id to PATCH, the id a create sends, and
//    the id the device draft is filed under.

export type EditorDraftStateInput = {
  single: boolean;
  program?: Program;
  plan?: Plan;
  /** #368: a new plan's first draft, instead of a blank one, in its unit. */
  planSeed?: PlanSeed;
  creationSeed?: NewProgram;
  unitsPreference?: UnitsPreference;
  resumeFrom?: EditorDraftBlob | null;
};

export function useEditorDraftState({
  single,
  program,
  plan,
  planSeed,
  creationSeed,
  unitsPreference,
  resumeFrom,
}: EditorDraftStateInput) {
  const server = single ? plan : program;
  // The unit the draft's absolute-weight cells are expressed in, FROZEN at
  // seed time. Weights are stored in kg; an lb user reads and types lb, so
  // the seed converts out and the save converts back, and the two have to
  // use the same unit or the round-trip multiplies every prescription by
  // 2.2. Reading the live prop in save() would do exactly that: the profile
  // starts as a synthetic kg DEFAULT and upgrades when the network lands
  // (ProfileContext `loaded`), so a draft seeded mid-load would be saved
  // against a unit it was never written in. A ref pins one answer for the
  // lifetime of the draft; a later preference change is picked up the next
  // time the editor is opened, which is the harmless direction to be wrong.
  // #336: a resumed draft keeps the unit it was written in.
  // #368: a seed was written in its own unit, the same way.
  const cellUnit = useRef<UnitsPreference>(
    resumeFrom?.unit ?? (single ? planSeed?.unit : undefined) ?? unitsPreference ?? DEFAULT_UNITS_PREFERENCE,
  ).current;
  // PE-21: only decides the editing language of a program that never
  // recorded an author; an existing program's own language wins.
  const lang = useLocale();
  // A server program or plan as the editor reads it: the one normalisation
  // the seed, the baseline and the resume's comparison all share.
  const snapshotOf = useCallback(
    (doc: Program | Plan): PersistedSnapshot =>
      single
        ? planToDraft(doc as Plan, cellUnit)
        : { draft: programToDraft(doc as Program, cellUnit, lang), scheduledFor: "" },
    [single, cellUnit, lang],
  );
  // What the server holds, as a draft. Seeds once per mount, like the
  // useState it replaced: later prop identities are host re-renders.
  const [snapshot, setSnapshot, history] = useHistoryState<PersistedSnapshot>(() => {
    if (resumeFrom) return resumeFrom.value;
    if (server) return snapshotOf(server);
    return single
      ? { draft: planSeed?.draft ?? blankPlanDraft(), scheduledFor: "" }
      : { draft: programToDraft(newProgramToProgram(creationSeed!), cellUnit, lang), scheduledFor: "" };
  });
  const setDraft = useCallback(
    (next: SetStateAction<Draft>) =>
      setSnapshot((s) => {
        const d = typeof next === "function" ? next(s.draft) : next;
        return d === s.draft ? s : { ...s, draft: d };
      }),
    [setSnapshot],
  );
  const setScheduledFor = useCallback(
    (next: string) =>
      setSnapshot((s) => (s.scheduledFor === next ? s : { ...s, scheduledFor: next })),
    [setSnapshot],
  );

  // The id to write to: the prop's, the resumed draft's, or one this editor
  // minted. null: nothing on the server yet, the next save is a create.
  const [serverId, setServerId] = useState<string | null>(
    () => server?.id ?? resumeFrom?.serverId ?? null,
  );
  // The id the device draft is filed under. It follows serverId only once a
  // write is CONFIRMED on the server (sent): a queued create may still be
  // refused, and a draft filed under an id no list shows would be lost. Until
  // then it stays under «new», where «+ New program» offers it back.
  const [draftId, setDraftId] = useState<string | null>(() => server?.id ?? null);
  // The id a create sends, minted when the editor opens and filed in the
  // device draft: a create retried, or resumed after the app was killed,
  // sends the SAME id, so a first attempt that did land answers 409 and the
  // save becomes an edit of it instead of a second program.
  const [mintedId] = useState<string | null>(() =>
    server ? null : (resumeFrom?.mintedId ?? resumeFrom?.serverId ?? uuidv4()),
  );

  /** A write landed (or was queued) under `id`. */
  const markWritten = useCallback((id: string, outcome: "sent" | "queued") => {
    setServerId(id);
    if (outcome === "sent") setDraftId(id);
  }, []);

  /** The device draft as it should be filed now, against `baseline`. */
  const draftBlob = useCallback(
    (baseline: PersistedSnapshot): EditorDraftBlob => ({
      unit: cellUnit,
      value: snapshot,
      baseline,
      serverId,
      mintedId,
    }),
    [cellUnit, snapshot, serverId, mintedId],
  );

  const undoRedo = useMemo(
    () => ({
      onUndo: history.undo,
      onRedo: history.redo,
      canUndo: history.canUndo,
      canRedo: history.canRedo,
    }),
    [history.undo, history.redo, history.canUndo, history.canRedo],
  );

  return {
    cellUnit,
    snapshot,
    setDraft,
    setScheduledFor,
    rebase: history.rebase,
    /** Replace the edits with `value` and forget the history (the resume's
     *  «take the server's version»). */
    resetTo: history.reset,
    /** Close the undo coalesce window (a save). */
    sealHistory: history.seal,
    undoRedo,
    serverId,
    mintedId,
    draftId,
    markWritten,
    snapshotOf,
    draftBlob,
  };
}
